import React, { useState } from 'react';
import { type ThreatProfile, type AppData } from '../../api/types.js';
import AppsField from './AppsField.js';
import SelectField from './SelectField.js';
import { CountryNames, ISO2to3Mapping } from '../../utils/Countries.js';
import { getLanguageName, isoLanguages } from '../../utils/Languages.js';
import Spinner from '../common/Spinner.js';
import { useTranslation } from '../../i18n/useTranslation.js';

const S = {
  field: {
    width: '100%',
    background: 'var(--bg2)',
    border: '1px solid var(--bm)',
    borderRadius: 4,
    color: 'var(--t1)',
    fontFamily: 'var(--mono)',
    fontSize: 11,
    padding: '5px 8px',
    outline: 'none',
    flex: 1,
    minWidth: 0,
  } as React.CSSProperties,
  addRow: {
    marginTop: 6,
  } as React.CSSProperties,
  tagRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 5,
    minHeight: 26,
  } as React.CSSProperties,
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    background: 'rgba(255,255,255,.06)',
    border: '1px solid var(--bm)',
    borderRadius: 12,
    color: 'var(--t2)',
    fontSize: 10,
    padding: '2px 8px 2px 6px',
    fontFamily: 'var(--mono)',
  } as React.CSSProperties,
  tagRm: {
    background: 'none',
    border: 'none',
    color: 'var(--t3)',
    cursor: 'pointer',
    fontSize: 12,
    lineHeight: 1,
    padding: 0,
    marginLeft: 2,
  } as React.CSSProperties,
  cancelBtn: {
    background: 'transparent',
    border: '1px solid var(--bm)',
    borderRadius: 4,
    color: 'var(--t2)',
    cursor: 'pointer',
    fontSize: 12,
    padding: '7px 16px',
  } as React.CSSProperties,
  appItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'rgba(255,255,255,.04)',
    border: '1px solid var(--bm)',
    borderRadius: 4,
    padding: '4px 8px',
    marginBottom: 4,
  } as React.CSSProperties,
  bundleLabel: {
    color: 'var(--t3)',
    fontFamily: 'var(--mono)',
    fontSize: 10,
  } as React.CSSProperties,
};

export interface ThreatProfileFormProps {
  profile?: ThreatProfile;
  onSave: (data: Omit<ThreatProfile, 'count'>) => void | Promise<void>;
  onCancel: () => void;
}

function ThreatProfileForm({ profile, onSave, onCancel }: ThreatProfileFormProps) {
  const { t } = useTranslation();
  const isEdit = !!profile;

  const [name, setName] = useState(profile?.name ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [languages, setLanguages] = useState<string[]>(profile?.hostileProps.languages ?? []);
  const [countries, setCountries] = useState<string[]>(profile?.hostileProps.countries ?? []);
  const [appBundles, setAppBundles] = useState<AppData[]>(profile?.hostileProps.appBundles ?? []);

  const [nameError, setNameError] = useState(false);

  const getLanguageOptions = () => {
    const languageOptions = Object.keys(isoLanguages).map((key) => ({
      value: key,
      label: `${getLanguageName(key)} (${key.toUpperCase()})`,
    }));
    return languageOptions.sort((a, b) => a.label?.localeCompare(b.label));
  };

  const getCountriesOptions = () => {
    const countries = Object.keys(ISO2to3Mapping);
    const countriesOptions = countries.map((cc2) => ({
      value: cc2,
      label: `${CountryNames[cc2]} (${cc2})`,
    }));
    return countriesOptions.sort((a, b) => a.label?.localeCompare(b.label));
  };

  /** Runs the save; the form stays usable (and reports the error) when the request fails. */
  const submit = async (action: () => void | Promise<void>, fallback: string) => {
    setIsSubmitting(true);
    setError('');
    try {
      await action();
    } catch (err) {
      setError((err as Error)?.message || fallback);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = () => {
    if (!name.trim()) {
      setNameError(true);
      return;
    }
    return submit(
      () =>
        onSave({
          id: profile?.id ?? `profile-${Date.now()}`,
          name: name.trim(),
          hostileProps: { languages, appBundles, countries },
        }),
      t('threatProfileForm.saveFailed')
    );
  };

  return (
    <div className="sp on" style={{ position: 'relative' }}>
      {isSubmitting && <Spinner size={28} />}
      <div className="sp-title">{isEdit ? t('threatProfileForm.editTitle') : t('threatProfileForm.newTitle')}</div>
      <div className="sp-subtitle">
        {isEdit ? t('threatProfileForm.editSubtitle', { name: profile.name }) : t('threatProfileForm.newSubtitle')}
      </div>

      {/* Profile Name */}
      <div className="sg">
        <div className="sg-title">{t('threatProfileForm.profileName')}</div>
        <div style={S.addRow}>
          <input
            style={{ ...S.field, borderColor: nameError ? 'var(--red)' : undefined }}
            value={name}
            disabled={isSubmitting}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError(false);
            }}
            placeholder={t('threatProfileForm.profileNamePlaceholder')}
          />
        </div>
        {nameError && (
          <div style={{ color: 'var(--red)', fontSize: 10, marginTop: 4 }}>
            {t('threatProfileForm.profileNameRequired')}
          </div>
        )}
      </div>

      {/* Device Languages */}
      <div className="sg">
        <div className="sg-title">{t('threatProfileForm.deviceLanguages')}</div>
        <div style={S.tagRow}>
          {languages.map((lang) => (
            <span key={lang} style={S.tag}>
              💬 {`${getLanguageName(lang)} (${lang.toUpperCase()})`}
              <button
                style={S.tagRm}
                onClick={() => setLanguages((prev) => prev.filter((l) => l !== lang))}
                disabled={isSubmitting}
              >
                ×
              </button>
            </span>
          ))}
          {languages.length === 0 && (
            <span style={{ color: 'var(--t3)', fontSize: 10, fontStyle: 'italic' }}>
              {t('threatProfileForm.noneAdded')}
            </span>
          )}
        </div>
        <div style={S.addRow}>
          <SelectField
            value={languages}
            options={getLanguageOptions()}
            disabled={isSubmitting}
            placeholder={t('threatProfileForm.langPlaceholder')}
            isSearchable={true}
            onAdd={(value) => setLanguages((prev) => [...prev, value])}
          />
        </div>
      </div>

      {/* Countries */}
      <div className="sg">
        <div className="sg-title">{t('threatProfileForm.countriesOfOrigin')}</div>
        <div style={S.tagRow}>
          {countries.map((cc2) => (
            <span key={cc2} style={S.tag}>
              🌍 {`${CountryNames[cc2]} (${cc2})`}
              <button
                style={S.tagRm}
                onClick={() => setCountries((prev) => prev.filter((c) => c !== cc2))}
                disabled={isSubmitting}
              >
                ×
              </button>
            </span>
          ))}
          {countries.length === 0 && (
            <span style={{ color: 'var(--t3)', fontSize: 10, fontStyle: 'italic' }}>
              {t('threatProfileForm.noneAdded')}
            </span>
          )}
        </div>
        <div style={S.addRow}>
          <SelectField
            value={countries}
            options={getCountriesOptions()}
            placeholder={t('threatProfileForm.countryPlaceholder')}
            isSearchable={true}
            disabled={isSubmitting}
            onAdd={(value) => setCountries((prev) => [...prev, value])}
          />
        </div>
      </div>

      {/* App Bundles */}
      <div className="sg">
        <div className="sg-title">{t('threatProfileForm.flaggedApps')}</div>
        <div>
          {appBundles.map(({ bundle, name: appName }) => {
            const [cleanBundle, os] = bundle.split('__');
            return (
              <div key={bundle} style={S.appItem}>
                <div>
                  <span style={{ color: 'var(--t1)', fontSize: 11 }}>
                    📱 {appName || cleanBundle}
                    {os && <span style={{ color: 'var(--t3)' }}> ({os})</span>}
                  </span>
                  <div style={S.bundleLabel}>{bundle.split('__')[0]}</div>
                </div>
                <button
                  style={S.tagRm}
                  onClick={() => setAppBundles((prev) => prev.filter((a) => a.bundle !== bundle))}
                  disabled={isSubmitting}
                >
                  ×
                </button>
              </div>
            );
          })}
          {appBundles.length === 0 && (
            <div style={{ color: 'var(--t3)', fontSize: 10, fontStyle: 'italic', marginBottom: 6 }}>
              {t('threatProfileForm.noneAdded')}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <AppsField
            name="hostileApps"
            value={appBundles.map((v) => v.bundle)}
            placeholder={t('threatProfileForm.appPlaceholder')}
            disabled={isSubmitting}
            onAdd={(value) =>
              setAppBundles((prev) => [
                ...prev,
                { bundle: `${value.id || value.bundle}__${value.os}`, name: value.name },
              ])
            }
          />
        </div>
      </div>

      {error && (
        <div className="acct-form-error" role="alert" style={{ marginTop: 4 }}>
          {error}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button className="save-btn" onClick={handleSave} disabled={isSubmitting}>
          {isEdit ? t('threatProfileForm.saveChanges') : t('threatProfileForm.createProfile')}
        </button>
        <button style={S.cancelBtn} onClick={onCancel} disabled={isSubmitting}>
          {t('threatProfileForm.cancel')}
        </button>
      </div>
    </div>
  );
}

export default ThreatProfileForm;
